import React, { useRef, useState } from 'react'
import { tosText } from './DashboardTOSText'
import { apiURL } from '../../../../utilities/URLs'
import { customerData } from '../../../../utilities/CustomerData'
import { refreshAccessToken } from '../../../../utilities/extensions'
import { useNavigate } from 'react-router-dom'

function MultilineText({ text }) {
  const lines = text.split('\\n')

  const renderTextWithBold = (text) => {
    const parts = text.split(/(<b>|<\/b>)/)
    let isInBold = false

    return parts.map((part, index) => {
      if (part === '<b>') {
        isInBold = true
        return null
      } else if (part === '</b>') {
        isInBold = false
        return null
      } else {
        return isInBold ? <b key={index}>{part}</b> : part
      }
    })
  }

  return (
    <div>
      {lines.map((line, index) => (
        <React.Fragment key={index}>
          {renderTextWithBold(line)}
          {index !== lines.length - 1 && <><br /><div style={{ height: '8px' }} /></>}
        </React.Fragment>
      ))}
    </div>
  )
}

export function DashboardTOSModal({ isOpen, setIsOpen, onClose }) {
  const navigate = useNavigate()

  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false)
  const [agreementChecked, setAgreementChecked] = useState(false)
  const [clientSignature, setClientSignature] = useState('')
  const [spouseSignature, setSpouseSignature] = useState('')
  const [clientDate, setClientDate] = useState(new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }))
  const [spouseDate, setSpouseDate] = useState(new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }))
  const [uploadingTOS, setUploadingTOS] = useState(false)
  const [uploadedTOS, setUploadedTOS] = useState(false)
  const contentRef = useRef(null)

  const handleScroll = () => {
    const content = contentRef.current
    if (content.scrollHeight - content.scrollTop === content.clientHeight) {
      setHasScrolledToEnd(true)
    }
  }

  const handleAccept = async () => {
    setUploadingTOS(true)

    const formData = new FormData()
    formData.append('customerTOSInfo', JSON.stringify({
      zohoId: customerData.customerLeadData.leadId,
      customerId: customerData.customerLeadData.leadContact.contactId,
      clientSignature: clientSignature,
      clientEmail: customerData.customerLeadData.leadContact.contactEmail,
      clientDate: clientDate,
      spouseSignature: spouseSignature,
      spouseDate: spouseDate
    }))

    const tosLinkResponse = await fetch(`${apiURL}/api/uploadTOS`, {
      method: 'POST',
      body: formData,
      headers: {
        'Authorization': customerData.accessToken
      }
    })
    const tosLinkData = await tosLinkResponse.json()
    setUploadingTOS(false)

    if (tosLinkData.response.error) {
      if (tosLinkData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleAccept() : setIsOpen(false); navigate('/', { state: { expired: true } })
      } else
        console.error(tosLinkData.response.error)
    } else {
      setUploadedTOS(true)
      setTimeout(() => {
        setUploadedTOS(false)
        customerData.customerTOSData = tosLinkData.response.customerTOSData
        onClose()
      }, 2000)
    }
  }

  const handleAgreementChange = (e) => {
    setAgreementChecked(e.target.checked)
  }

  return (
    <div className={`fixed z-50 flex justify-center items-center top-0 left-0 w-full h-full bg-gray-900 bg-opacity-50 ${isOpen ? '' : 'hidden'}`}>
      <div className='relative w-10/12 h-[90%] max-sm:mr-6 max-sm:mb-12 p-6 font-mont bg-white rounded-md shadow-lg overflow-y-auto'>
        <button onClick={() => setIsOpen(false)} className='absolute lg:right-8 right-3 max-sm:top-3 text-red-700 hover:text-red-900 transition-colors duration-200'>
          <svg xmlns='http://www.w3.org/2000/svg' className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
            <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M6 18L18 6M6 6l12 12' />
          </svg>
        </button>
        <p className='font-bold text-center text-3xl max-sm:text-xl mb-10 max-sm:mb-4'>SETC Pros Client Agreement</p>
        <div
          ref={contentRef}
          onScroll={handleScroll}
          className={`${hasScrolledToEnd === true ? 'h-[70%]' : 'h-[80%] max-sm:h-[70%]'} overflow-y-scroll border border-gray-300 p-4 rounded-md`}
        >
          <h2 className='text-2xl font-bold mb-4'>Terms of Service</h2>
          <MultilineText text={tosText} />
          <div className='mt-12 flex items-center'>
            <input
              type='checkbox'
              id='agreement'
              className='mr-2 lg:w-4 lg:h-4 w-6 h-6'
              onChange={handleAgreementChange}
            />
            <label htmlFor='agreement'>
              I agree to the SETC Pros Client Agreement
            </label>
          </div>
        </div>
        {agreementChecked ? (
          <div className='mt-6'>
            <div className='mb-4'>
              <label className='block mb-2 font-medium'>Client Signature</label>
              <input
                type='text'
                placeholder='Sign Your Name'
                value={clientSignature}
                onChange={(e) => setClientSignature(e.target.value)}
                className='w-full p-2 border border-gray-300 rounded-md'
                required
              />
              <label className='block mb-2 font-medium mt-4'>Client Date</label>
              <input
                type='text'
                placeholder='Enter Date'
                value={clientDate}
                readOnly
                className='w-full p-2 border border-gray-300 rounded-md'
              />
            </div>
            {customerData.customerDocuments.marriedFilingJoint && (
              <div className='mb-4'>
                <label className='block mb-2 font-medium'>Spouse Signature</label>
                <input
                  type='text'
                  placeholder='Sign Your Name'
                  value={spouseSignature}
                  onChange={(e) => setSpouseSignature(e.target.value)}
                  className='w-full p-2 border border-gray-300 rounded-md'
                />
                <label className='block mb-2 font-medium mt-4'>Spouse Date</label>
                <input
                  type='text'
                  placeholder='Enter Date'
                  value={spouseDate}
                  readOnly
                  className='w-full p-2 border border-gray-300 rounded-md'
                />
              </div>
            )}
            {uploadingTOS && (
              <div className='flex flex-row items-center justify-center gap-2'>
                <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
                <p>Uploading SETC Pros Client Agreement...</p>
              </div>
            )}
            {uploadedTOS && (
              <p className='text-center mb-4'>SETC Pros Client Agreement Uploaded Successfully, Thank You!</p>
            )}
            <button
              onClick={handleAccept}
              disabled={(!customerData.customerDocuments.marriedFilingJoint ? !clientSignature : !clientSignature || !spouseSignature) || uploadingTOS}
              className={`w-full p-2 rounded-md text-white ${((!customerData.customerDocuments.marriedFilingJoint && clientSignature) ||
                  (customerData.customerDocuments.marriedFilingJoint && clientSignature && spouseSignature))
                  && !uploadingTOS
                  ? 'bg-blue-500 hover:bg-blue-600'
                  : 'bg-gray-400 cursor-not-allowed'
                }`}
            >
              Accept Terms
            </button>
          </div>
        ) : (
          <p className='mt-6 text-gray-600'>Please scroll to the bottom to accept the terms.</p>
        )}
      </div>
    </div>
  )
}

export default DashboardTOSModal
